import { el } from "./dom.js";
import { magButton } from "./magazine/kit.js";
import { showToast } from "./toast.js";
import { overriddenFields } from "../utils/overrides.js";
import {
	setOverrides,
	clearAllOverrides,
	relinkItem,
	undoRelink,
} from "../db/overrides.js";
import { showEditDetailsSheet } from "./edit-details-sheet.js";
import { showRelinkSheet } from "./relink-sheet.js";

// The "⋯" menu on a detail hero (game / book / TV / anime): manual corrections to
// catalog data. Each action does its write and then calls
// `onChanged(updatedRecord, { relinked })` so the view can re-mount — a re-link
// swaps the catalog underneath, so views rebuild the whole route for that one.

const SOURCE_LABEL = {
	videogame: "IGDB",
	book: "Open Library",
	tv: "TVmaze",
	anime: "TVmaze",
	movie: "OMDb",
	comic: "Metron",
};
// Re-link isn't offered for comics yet: a comic can move between two sources
// (Metron ⇄ Open Library), which the single-source re-link flow doesn't model.
const NO_RELINK = new Set(["comic"]);

// A vertical list of actions in a modal. Resolves once closed; the chosen
// action's `run` fires after the sheet is gone (so its own modal can open).
export function showActionSheet(title, actions) {
	return new Promise((resolve) => {
		let settled = false;
		const finish = (run) => {
			if (settled) return;
			settled = true;
			overlay.remove();
			document.removeEventListener("keydown", onKey);
			resolve();
			if (run) run();
		};
		const onKey = (e) => {
			if (e.key === "Escape") finish(null);
		};
		document.addEventListener("keydown", onKey);

		const overlay = el(
			"div.modal-overlay",
			{
				onclick: (e) => {
					if (e.target === overlay) finish(null);
				},
			},
			el(
				"div.modal",
				{},
				el("h2.modal__title", {}, title),
				el(
					"div",
					{ style: { display: "flex", flexDirection: "column", gap: "8px", marginTop: "10px" } },
					...actions.map((a) =>
						magButton(a.label, {
							variant: a.variant ?? "ghost",
							block: true,
							onClick: () => finish(a.run),
						}),
					),
					magButton("Cancel", {
						variant: "ghost",
						block: true,
						onClick: () => finish(null),
						style: { opacity: ".7" },
					}),
				),
			),
		);
		document.body.appendChild(overlay);
	});
}

export function openItemMenu({ mediaType, record, catalog = null, onChanged }) {
	const source = SOURCE_LABEL[mediaType] ?? "source";
	const actions = [
		{
			label: "Edit details",
			run: async () => {
				const patch = await showEditDetailsSheet({ mediaType, record, catalog });
				if (!patch) return;
				try {
					onChanged(await setOverrides(mediaType, record.id, patch));
				} catch (err) {
					showToast(err?.message ?? "Could not save.");
				}
			},
		},
		!NO_RELINK.has(mediaType) && {
			label: `Re-link to another ${source} entry`,
			run: async () => {
				const picked = await showRelinkSheet({ mediaType, record });
				if (!picked) return;
				try {
					const next = await relinkItem(mediaType, record.id, picked.fresh, {
						taxonomy: picked.taxonomy,
					});
					if (mediaType === "anime" && picked.enrichFailed) {
						showToast("Genres kept from the previous match — the MAL lookup failed.");
					}
					onChanged(next, { relinked: true });
				} catch (err) {
					showToast(err?.message ?? "Could not re-link.");
				}
			},
		},
	].filter(Boolean);
	if (record.relinked_from?.external_id) {
		const back = record.relinked_from.title ? `"${record.relinked_from.title}"` : "the previous entry";
		actions.push({
			label: `Undo re-link (back to ${back})`,
			run: async () => {
				try {
					onChanged(await undoRelink(mediaType, record.id), { relinked: true });
				} catch (err) {
					showToast(err?.message ?? "Could not undo.");
				}
			},
		});
	}
	if (overriddenFields(record).length > 0) {
		actions.push({
			label: "Reset details to catalog",
			run: async () => {
				try {
					onChanged(await clearAllOverrides(mediaType, record.id));
				} catch (err) {
					showToast(err?.message ?? "Could not reset.");
				}
			},
		});
	}
	return showActionSheet(record.title ?? "This item", actions);
}
